import useSWR from 'swr'

import { FetchError } from '../../../data/fetchError'
import { FetchResponse } from '../../../data/fetchResponse'
import { FetchResult } from '../../../data/fetchResult'
import { fetchJson } from '../../../data/fetchers'
import {
  DeliveryForecast,
  DeliveryForecastItem,
  DeliveryForecastItemForecast,
  DeliveryForecastItemSplit,
  WebEdiDeliveryForecastDto,
} from '../../../data/models'
import { useConfigContext } from '../../ui-config'
import { DelForSettingsDto } from './model'

export function useDeliveryForecastById(
  id: string,
): FetchResult<WebEdiDeliveryForecastDto<DeliveryForecast>> {
  const { pollingConfig } = useConfigContext()
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<WebEdiDeliveryForecastDto<DeliveryForecast>>,
    FetchError
  >(
    `/deliveryforecasts/${id}`,
    fetchJson<WebEdiDeliveryForecastDto<DeliveryForecast>>,
    {
      refreshInterval: pollingConfig.intervalInSeconds * 1000,
    },
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useAllDeliveryForecastItems(
  deliveryForecastId: string,
): FetchResult<DeliveryForecastItem[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItem[]>,
    FetchError
  >(
    `/deliveryforecasts/${deliveryForecastId}/items`,
    fetchJson<DeliveryForecastItem[]>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastItemById(
  deliveryForecastId: string,
  id: string,
): FetchResult<DeliveryForecastItem> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItem>,
    FetchError
  >(
    `/deliveryforecasts/${deliveryForecastId}/items/${id}`,
    fetchJson<DeliveryForecastItem>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useAllDeliveryForecastItemForecasts(
  deliveryForecastId: string,
  deliveryForecastItemId: string,
): FetchResult<DeliveryForecastItemForecast[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItemForecast[]>,
    FetchError
  >(
    `/deliveryforecasts/${deliveryForecastId}/items/${deliveryForecastItemId}/forecasts`,
    fetchJson<DeliveryForecastItemForecast[]>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastItemForecastById(
  deliveryForecastId: string,
  deliveryForecastItemId: string,
  id: string,
): FetchResult<DeliveryForecastItemForecast> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItemForecast>,
    FetchError
  >(
    `/deliveryforecasts/${deliveryForecastId}/items/${deliveryForecastItemId}/forecasts/${id}`,
    fetchJson<DeliveryForecastItemForecast>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useAllDeliveryForecastItemSplits(
  deliveryForecastId: string,
  deliveryForecastItemId: string,
): FetchResult<DeliveryForecastItemSplit[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItemSplit[]>,
    FetchError
  >(
    `/deliveryforecasts/${deliveryForecastId}/items/${deliveryForecastItemId}/splits`,
    fetchJson<DeliveryForecastItemSplit[]>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastItemSplitsById(
  deliveryForecastId: string,
  deliveryForecastItemId: string,
  id: string,
): FetchResult<DeliveryForecastItemSplit> {
  const shouldFetch = deliveryForecastId && deliveryForecastItemId && id
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastItemSplit>,
    FetchError
  >(
    shouldFetch
      ? `/deliveryforecasts/${deliveryForecastId}/items/${deliveryForecastItemId}/splits/${id}`
      : null,
    fetchJson<DeliveryForecastItemSplit>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDelForSettings(): FetchResult<DelForSettingsDto> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DelForSettingsDto>,
    FetchError
  >(`settings/deliveryforecast`, fetchJson<DelForSettingsDto>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}
